
import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Assessment } from "@/components/Auth/Folders/FolderContentsTypes";
import { Folder } from "../../../types";
import { toast } from "sonner";
import { useAuth } from "@/context/auth";

export interface UseDataFetchingProps {
  folderId: string;
  projectId?: string;
}

export function useDataFetching({ folderId, projectId }: UseDataFetchingProps) {
  const { user } = useAuth();
  const [folder, setFolder] = useState<Folder | null>(null);
  const [parentFolder, setParentFolder] = useState<Folder | null>(null);
  const [subfolders, setSubfolders] = useState<Folder[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [shareDetails, setShareDetails] = useState<any | null>(null);
  const [owner, setOwner] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const refreshAttempts = useRef(0);
  const lastRefreshTime = useRef<number>(0);

  const fetchData = useCallback(async () => {
    if (!user) {
      console.warn("No user found when fetching folder data");
      setIsLoading(false);
      return;
    }

    console.log('🔍 FETCHING DATA FOR FOLDER:', { folderId, projectId, timestamp: new Date().toISOString() });
    
    if (!folderId) {
      console.error('❌ NO FOLDER ID PROVIDED');
      setError("No folder ID provided");
      setIsLoading(false);
      return;
    }

    // Prevent excessive refresh attempts - more strict limits
    const now = Date.now();
    if (now - lastRefreshTime.current < 5000 && refreshAttempts.current > 2) {
      console.log("Too many refresh attempts, backing off...");
      setIsLoading(false);
      return;
    }
    
    lastRefreshTime.current = now;
    refreshAttempts.current++;

    setIsLoading(true);
    setError(null);

    try {
      // Fetch current folder info
      const { data: folderData, error: folderError } = await supabase
        .from("folders")
        .select("*")
        .eq("id", folderId)
        .single();

      if (folderError) {
        throw new Error(`Could not fetch folder: ${folderError.message}`);
      }

      setFolder(folderData);
      setParentFolder(folderData);

      // Fetch subfolders
      const { data: subfoldersData, error: subfoldersError } = await supabase
        .from("folders")
        .select("*")
        .eq("parent_folder_id", folderId)
        .order("name");

      if (subfoldersError) {
        throw new Error(`Could not fetch subfolders: ${subfoldersError.message}`);
      }

      // Fetch risk assessments
      const { data: riskAssessmentsData, error: riskAssessmentsError } = await supabase
        .from("folder_risk_assessments")
        .select("*")
        .eq("folder_id", folderId)
        .order("name");

      if (riskAssessmentsError) {
        throw new Error(`Could not fetch risk assessments: ${riskAssessmentsError.message}`);
      }

      // Fetch SKR assessments with enhanced debugging and RLS testing
      console.log('📋 FETCHING SKR ASSESSMENTS for folder:', folderId);
      console.log('🔍 Auth user ID:', (await supabase.auth.getUser()).data.user?.id);
      
      // Test user_has_folder_access function first
      const { data: accessTest, error: accessError } = await supabase
        .rpc('user_has_folder_access', { folder_uuid: folderId });
      
      console.log('🔐 user_has_folder_access result:', accessTest, 'error:', accessError);
      
      const { data: skrAssessmentsData, error: skrAssessmentsError } = await supabase
        .from("skr_assessments")
        .select("*")
        .eq("folder_id", folderId)
        .order("name");

      console.log('🔍 SKR ASSESSMENTS RAW RESULT:', { skrAssessmentsData, skrAssessmentsError, count: skrAssessmentsData?.length });
      
      // If we have an error, provide more detailed debugging
      if (skrAssessmentsError) {
        console.error('❌ SKR ASSESSMENTS ERROR DETAILS:', {
          message: skrAssessmentsError.message,
          details: skrAssessmentsError.details,
          hint: skrAssessmentsError.hint,
          code: skrAssessmentsError.code
        });
        
        // Test if we can read from skr_assessments at all
        const { data: allSkr, error: allSkrError } = await supabase
          .from('skr_assessments')
          .select('id, name, folder_id, created_by')
          .limit(5);
        
        console.log('🔍 All accessible SKR assessments test:', allSkr, 'error:', allSkrError);
        
        throw new Error(`Could not fetch SKR assessments: ${skrAssessmentsError.message}`);
      }

      // Fetch work preparations
      const { data: workPreparationsData, error: workPreparationsError } = await supabase
        .from("work_preparations")
        .select("*")
        .eq("folder_id", folderId)
        .order("name");

      if (workPreparationsError) {
        throw new Error(`Could not fetch work preparations: ${workPreparationsError.message}`);
      }

      // Filter out SKR assessments from folder_risk_assessments to prevent duplicates
      console.log('🔍 BEFORE FILTER - Risk assessments raw:', (riskAssessmentsData || []).map(a => ({ 
        id: a.id, 
        name: a.name, 
        assessment_id: a.assessment_id,
        isSkrId: a.assessment_id?.startsWith('skr_assessment_')
      })));
      
      const filteredRiskAssessments = (riskAssessmentsData || []).filter(assessment => {
        const hasAssessmentId = Boolean(assessment.assessment_id);
        const isSkrAssessment = hasAssessmentId && assessment.assessment_id.startsWith('skr_assessment_');
        const shouldInclude = !isSkrAssessment;
        
        console.log('🔍 FILTER CHECK:', {
          id: assessment.id,
          name: assessment.name,
          assessment_id: assessment.assessment_id,
          hasAssessmentId,
          isSkrAssessment,
          shouldInclude
        });
        
        return shouldInclude;
      });

      console.log('🔍 AFTER FILTER - Filtered risk assessments:', filteredRiskAssessments.map(a => ({ 
        id: a.id, 
        name: a.name, 
        assessment_id: a.assessment_id 
      })));

      // Function to determine correct type based on assessment_id (for remaining risk assessments)
      const getCorrectAssessmentType = (assessment: any): 'risk_assessment' | 'work_preparation' => {
        if (assessment.assessment_id && assessment.assessment_id.startsWith('work_preparation_')) {
          return 'work_preparation';
        }
        return 'risk_assessment';
      };

      // Combine and normalize assessments with proper type assignment
      const combinedAssessments: Assessment[] = [
        // Pure risk assessments (SKR assessments filtered out)
        ...filteredRiskAssessments.map(assessment => {
          const mappedAssessment = {
            id: assessment.id,
            folder_id: assessment.folder_id,
            assessment_id: assessment.assessment_id,
            name: assessment.name,
            description: assessment.description || '',
            created_at: assessment.created_at,
            updated_at: assessment.updated_at || assessment.created_at,
            data: assessment.data,
            type: getCorrectAssessmentType(assessment),
            project_id: assessment.project_id
          };
          
          // 🔍 DEBUG: Log each risk assessment mapping
          console.log('🔍 Risk Assessment Mapping:', {
            originalId: assessment.id,
            assessmentId: assessment.assessment_id,
            name: assessment.name,
            assignedType: mappedAssessment.type
          });
          
          return mappedAssessment;
        }),
        // SKR assessments from dedicated table - FORCE EXPLICIT TYPE
        ...(skrAssessmentsData || []).map(assessment => {
          const timestamp = new Date().toISOString().slice(11, 23);
          const mappedSKR = {
            id: assessment.id,
            folder_id: assessment.folder_id,
            assessment_id: assessment.assessment_id,
            name: assessment.name,
            description: assessment.description || '',
            created_at: assessment.created_at,
            updated_at: assessment.updated_at,
            data: assessment.data,
            type: 'skr_assessment' as const,
            project_id: assessment.project_id
          };
          
          // Force logging with timestamp
          console.log(`🔍 [${timestamp}] SKR MAPPING:`, assessment.name, '| TYPE SET TO:', mappedSKR.type);
          console.log(`🔍 [${timestamp}] SKR DETAILED:`, {
            name: assessment.name,
            assignedType: mappedSKR.type,
            typeIsCorrect: mappedSKR.type === 'skr_assessment',
            originalData: assessment
          });
          
          return mappedSKR;
        }),
        // Work preparations with type marking
        ...(workPreparationsData || []).map(assessment => ({
          id: assessment.id,
          folder_id: assessment.folder_id,
          assessment_id: assessment.id, // Use id as assessment_id for work preparations
          name: assessment.name,
          description: assessment.name, // Use name as description fallback
          created_at: assessment.created_at,
          updated_at: assessment.updated_at,
          data: assessment.data,
          type: 'work_preparation' as const,
          project_id: assessment.project_id,
          source_risk_assessment_id: assessment.source_risk_assessment_id,
          status: assessment.status
        }))
      ];

      // 🔍 FINAL DEBUG: Summary of all assessments before returning
      console.log('📊 FINAL ASSESSMENT SUMMARY:', {
        totalAssessments: combinedAssessments.length,
        skrCount: combinedAssessments.filter(a => a.type === 'skr_assessment').length,
        riskCount: combinedAssessments.filter(a => a.type === 'risk_assessment').length,
        workPrepCount: combinedAssessments.filter(a => a.type === 'work_preparation').length,
        detailedList: combinedAssessments.map(a => ({ 
          id: a.id, 
          name: a.name, 
          type: a.type,
          assessment_id: a.assessment_id
        }))
      });

      const hasSubfolders = subfoldersData && subfoldersData.length > 0;
      const hasAssessments = combinedAssessments && combinedAssessments.length > 0;
      
      setSubfolders(subfoldersData || []);
      setAssessments(combinedAssessments);
      setIsEmpty(!hasSubfolders && !hasAssessments);

      // Reset refresh attempts on successful fetch
      refreshAttempts.current = 0;

      // Fetch share information if available
      const { data: shareData, error: shareError } = await supabase
        .from("folder_shares")
        .select("*")
        .eq("folder_id", folderId)
        .order("created_at", { ascending: false });

      if (!shareError && shareData && shareData.length > 0) {
        setShareDetails(shareData[0]);
        
        if (shareData[0].owner_id) {
          const { data: ownerData } = await supabase
            .from("user_profiles")
            .select("*")
            .eq("id", shareData[0].owner_id)
            .single();
            
          if (ownerData) {
            setOwner(ownerData);
          }
        }
      }

    } catch (err: any) {
      console.error("Error fetching folder content:", err);
      setError(err.message);
      
      // Only show toast for first attempt to avoid spam
      if (refreshAttempts.current === 1) {
        toast.error("Kunde inte hämta mappinnehåll", {
          description: err.message
        });
      }
      
      // Exponential backoff for retries
      if (refreshAttempts.current <= 2) {
        const backoffDelay = Math.min(2000 * Math.pow(2, refreshAttempts.current - 1), 10000);
        setTimeout(() => {
          if (refreshAttempts.current <= 2) {
            fetchData();
          }
        }, backoffDelay);
      }
    } finally {
      setIsLoading(false);
    }
  }, [folderId, user, projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    folder,
    parentFolder,
    subfolders,
    assessments,
    shareDetails,
    owner,
    error,
    isLoading,
    isEmpty,
    refetch: fetchData,
  };
}
